import Hls from 'hls.js';
import {IntlShape} from 'react-intl';
import {VideoMonitor} from './VideoStreamMonitor';
import {VideoStreamSubject} from './VideoStreamSubject';

export class HlsClient {
  static RETRY_PAUSE: number = 2000;
  static RETRY_ATTEMPTS: number = 30;

  hlsRef?: Hls | null;
  intl: IntlShape;
  monitor: VideoMonitor;
  interrupted: boolean = false;
  connected: boolean = false;
  videoUnavailableMessageKey: string = 'stream.video.url.unavailable';

  constructor(intl: IntlShape, monitor: VideoMonitor) {
    this.intl = intl;
    this.monitor = monitor;
  }

  closeConnection(): void {
    if (this.hlsRef) {
      this.hlsRef.detachMedia();
      this.hlsRef.destroy();
      this.hlsRef = null;
      this.connected = false;
      console.log('HLS connection deregistered');
    }
  }

  openConnection(
    url: string,
    subject: VideoStreamSubject,
    attempts = HlsClient.RETRY_ATTEMPTS
  ): void {
    if (this.interrupted) {
      return;
    }
    var hlsRef: Hls;
    if (attempts === 0) {
      console.log('HLS: no reconnection attempts remaining. Giving up.');
      subject.raiseInitErrorEvent(
        this.intl.formatMessage({id: this.videoUnavailableMessageKey})
      );
      return;
    }
    if (Hls.isSupported()) {
      hlsRef = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        fragLoadingTimeOut: 30000,
        manifestLoadingTimeOut: 30000,
        levelLoadingTimeOut: 30000,
        manifestLoadingMaxRetry: 6,
      });

      hlsRef.on(Hls.Events.ERROR, (evt, data) => {
        if (this.interrupted) {
          return;
        }
        if (data.fatal) {
          if (data.details === 'manifestIncompatibleCodecsError') {
            hlsRef.destroy();
            subject.raiseInitErrorEvent(
              this.intl.formatMessage({id: 'stream.video.codec.not.supported'})
            );
          } else if (data.response && data.response.code === 404) {
            if (this.connected) {
              hlsRef.startLoad();
            } else {
              hlsRef.destroy();
              subject.raiseConnectionFailedEvent();
              subject.raiseConnectingEvent();
              console.log(
                `HLS: reconnection after 404, attempts remaining: ${attempts - 1}`
              );
              setTimeout(
                () => this.openConnection(url, subject, attempts - 1),
                HlsClient.RETRY_PAUSE
              );
            }
          } else {
            hlsRef.destroy();
            subject.raiseInitErrorEvent(data.error + ' (' + data.details + ')');
          }
        }
      });

      hlsRef.on(Hls.Events.MEDIA_ATTACHED, () => {
        if (this.interrupted) {
          return;
        }
        console.log('HLS: MEDIA_ATTACHED');
        hlsRef.loadSource(url);
      });

      hlsRef.on(Hls.Events.MANIFEST_PARSED, () => {
        if (this.interrupted) {
          return;
        }
        console.log('HLS: MANIFEST_PARSED');
        this.connected = true;
        subject.raiseStreamReadyEvent();
      });

      hlsRef.on(Hls.Events.BUFFER_EOS, () => {
        if (this.interrupted) {
          return;
        }
        console.log('HLS: BUFFER_EOS');
        subject.raiseStreamFinishEvent();
      });

      this.hlsRef = hlsRef;
      console.log('HLS connection registered');
      subject.raiseConnectedEvent();

      if (this.monitor.config?.monitorStream) {
        hlsRef.on(Hls.Events.FRAG_BUFFERED, (eventName, data) => {
          this.monitor.kbps = Math.round(
            (8 * data.stats.total) / (data.stats.buffering.end - data.stats.loading.start)
          );
        });
      }
    } else {
      subject.raiseInitErrorEvent(
        this.intl.formatMessage({id: 'stream.video.hls.not.supported'})
      );
    }
  }
}
